/**
 * THE rule behind the mobile access gate — the one place that decides what a visitor sees.
 *
 * Spec: olympus-functions/.scratch/mobile-access-mode/spec.md
 *
 * Pure on purpose: no DOM, no network, no Angular. Every combination of mode, platform, install state
 * and browser is therefore checkable without a browser, which is the only way a rule with this many
 * cases stays trustworthy.
 *
 * ─── WHAT THIS DOES NOT DO ───────────────────────────────────────────────────────────────────────
 * It HIDES the app; it does not secure it. The application's code still reaches the browser. If the
 * goal were keeping people away from data, this is the wrong tool and authentication is the right one.
 *
 * The service worker's register/unregister answer is NOT here — it lives in serviceWorkerAction() in
 * service-worker.util.ts, where it shipped with the worker itself. One question, one home.
 */
import { MobileAccessMode } from './service-worker.util';

export type DevicePlatform = 'ios' | 'android' | 'other';
export type BrowserKind = 'in-app' | 'normal';

export interface GateInput {
  /** Absent when resolveHost did not answer inside its deadline — see the fail-open note below. */
  mode?: MobileAccessMode;
  /** isMobilePlatform() — phones AND tablets. Never a viewport test. */
  isMobile: boolean;
  /** Capacitor.isNativePlatform() — the project's own Android app, running its own WebView. */
  native: boolean;
  /** navigator.standalone, or the standalone display-mode media query. */
  standalone: boolean;
  platform: DevicePlatform;
  browser: BrowserKind;
}

export type GateDecision =
  | { screen: 'app' }
  | { screen: 'install'; platform: DevicePlatform; browser: BrowserKind }
  | { screen: 'unavailable' };

export function mobileAccessDecision(i: GateInput): GateDecision {
  // FAIL OPEN. An unknown mode means the boot-path call to resolveHost did not answer in time. A gate
  // that failed closed would turn one slow Cloud Function into a fleet-wide lockout that looks like a
  // deliberate act by the builder. This setting shapes a habit; it does not protect anything, so it
  // does not deserve to fail dangerous.
  if (i.mode !== 'pwa_only' && i.mode !== 'native_only') return { screen: 'app' };

  // Desktop has an entirely different flow — its own landing page and its own pages — and there is no
  // sane install story for it. Gating it would lock out the people doing the work.
  if (!i.isMobile) return { screen: 'app' };

  // The project's own native app, running the same code in its own WebView. Gating here would have an
  // installed app demand that the user install it.
  if (i.native) return { screen: 'app' };

  // NATIVE ONLY IGNORES INSTALLED-NESS, deliberately, and this check must stay above the standalone
  // one below. Under this mode the web app is not a client at all, so an installed web app is refused
  // exactly like a browser tab. Someone who installed while the mode was pwa_only keeps an icon that
  // now refuses them — the owner's decision, which the rule must not soften.
  if (i.mode === 'native_only') return { screen: 'unavailable' };

  if (i.standalone) return { screen: 'app' };

  return { screen: 'install', platform: i.platform, browser: i.browser };
}

/**
 * Is this someone else's browser, embedded in their app?
 *
 * It matters because on iOS **Add to Home Screen does not exist** inside an embedded WKWebView — the
 * option we are about to describe is simply not in that share sheet. Links get shared over WhatsApp,
 * so this is not a corner case; it is how a first-time visitor most often arrives.
 *
 * HEURISTIC, AND KNOWN TO BE. New apps ship, user-agent strings change. The escape hatch is rendered
 * regardless of what this returns — detection only decides what appears FIRST — so a wrong answer
 * costs a line of irrelevant text rather than locking anyone out. That asymmetry is why an unknown
 * agent answers `normal`: guessing `in-app` would put escape copy in front of people who do not need it.
 */
export function detectBrowser(ua: string): BrowserKind {
  const s = ua || '';
  if (!s) return 'normal';
  // Named apps first — these are unambiguous.
  if (/FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|MicroMessenger|WhatsApp|Twitter/i.test(s)) return 'in-app';
  // Android marks its WebView with a `wv` token in the platform section.
  if (/;\s*wv\)/i.test(s)) return 'in-app';
  // iOS: real Safari carries a `Safari/` token; an embedded WKWebView usually does not.
  if (/iPhone|iPad|iPod/i.test(s) && !/Safari\//i.test(s)) return 'in-app';
  return 'normal';
}

// ─── COPY ────────────────────────────────────────────────────────────────────────────────────────
// Exported rather than typed into a template, so the words are testable and live beside the rule that
// chooses between them. The same discipline as workspace-rules.ts in olympus-functions.

export const INSTALL_TITLE = 'Install this app to continue';

export const IOS_STEPS = [
  'Tap the Share button at the bottom of the screen.',
  'Scroll down and choose "Add to Home Screen".',
  'Tap Add, then open the app from your home screen.',
];

export const ANDROID_INTRO = 'Install the app to your home screen to continue.';
export const ANDROID_BUTTON = 'Install app';

/** Shown when Chrome has not offered an install prompt, so the button has something to say. */
export const ANDROID_FALLBACK_STEPS = [
  'Tap the ⋮ menu at the top right of Chrome.',
  'Choose "Add to Home screen" or "Install app".',
  'Open the app from your home screen.',
];

/** Always shown. See detectBrowser: this is the line that stops the gate being a wall. */
export const ESCAPE_IOS =
  "Don't see that option? Open this page in Safari first — tap the ⋯ menu and choose Open in Safari.";
export const ESCAPE_ANDROID =
  "Don't see that option? Open this page in Chrome first — tap the ⋮ menu and choose Open in Chrome.";

export const UNAVAILABLE_TITLE = 'Not available here';
export const UNAVAILABLE_BODY = 'This app is only available in its mobile app.';
